import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { HiMagnifyingGlass, HiOutlinePlus, HiOutlineXMark, HiOutlinePencilSquare, HiOutlineFunnel } from "react-icons/hi2";

import api from "../../../../services/api";
import Loader from "../../../../components/loader";
import Pagination from "../../../../components/pagination";
import DebounceInput from "react-debounce-input";

const CATEGORIES = ["challenge", "lesson", "profil", "admin", "draft", "mortem"];
const TYPES = ["book", "tutorial", "mooc", "other"];
const LEVELS = ["beginner", "intermediate", "advanced"];
const AXES = ["code", "leadership", "communication", "business", "productivity", "investment", "product", "gtm", "data"];

const categoryColors = {
  challenge: "bg-red-100 text-red-700",
  lesson: "bg-blue-100 text-blue-700",
  profil: "bg-green-100 text-green-700",
  admin: "bg-purple-100 text-purple-700",
  draft: "bg-gray-100 text-gray-500",
  mortem: "bg-amber-100 text-amber-700",
};

const levelColors = {
  beginner: "bg-emerald-100 text-emerald-700",
  intermediate: "bg-amber-100 text-amber-700",
  advanced: "bg-red-100 text-red-700",
};

const PER_PAGE = 25;

export default function List() {
  const [workshops, setWorkshops] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = {
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "",
    type: searchParams.get("type") || "",
    level: searchParams.get("level") || "",
    axe: searchParams.get("axe") || "",
  };
  const setFilters = (updaterFn) => {
    const newFilters = updaterFn(filters);
    setSearchParams(newFilters);
    setPage(1);
  };
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    load();
  }, [searchParams, page]);

  async function load() {
    try {
      setLoading(true);
      const body = {
        search: filters.search || undefined,
        category: filters.category || undefined,
        type: filters.type || undefined,
        level: filters.level || undefined,
        axe: filters.axe || undefined,
        page,
        per_page: PER_PAGE,
        short: true,
      };
      const { data, ok, total: totalCount } = await api.post("/workshop/search", body);
      if (!ok) throw new Error("Failed to load workshops");
      setWorkshops(data || []);
      setTotal(totalCount || 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  const hasActiveFilters = filters.category || filters.type || filters.level || filters.axe;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <HiOutlinePencilSquare className="w-8 h-8 text-blue-600" />
              Workshop Editor
            </h1>
            <p className="text-gray-600">Create, edit and manage your workshop content</p>
          </div>
          <Create />
        </div>

        {/* Search + Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
              </div>
              <DebounceInput
                debounceTimeout={300}
                className="block w-full rounded-lg border-gray-200 bg-gray-50 pl-10 py-2.5 text-sm focus:border-blue-500 focus:ring-blue-500 focus:bg-white transition-colors"
                placeholder="Search workshops..."
                value={filters.search}
                onChange={(e) => {
                  e.persist();
                  setFilters((f) => ({ ...f, search: e.target.value }));
                }}
              />
            </div>

            <FilterSelect
              label="Category"
              value={filters.category}
              options={CATEGORIES}
              onChange={(val) => setFilters((f) => ({ ...f, category: val }))}
            />
            <FilterSelect label="Type" value={filters.type} options={TYPES} onChange={(val) => setFilters((f) => ({ ...f, type: val }))} />
            <FilterSelect label="Level" value={filters.level} options={LEVELS} onChange={(val) => setFilters((f) => ({ ...f, level: val }))} />
            <FilterSelect label="Axe" value={filters.axe} options={AXES} onChange={(val) => setFilters((f) => ({ ...f, axe: val }))} />

            {hasActiveFilters && (
              <button
                onClick={() => setSearchParams({})}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 px-2 py-1 rounded-md hover:bg-red-50 transition-colors">
                <HiOutlineXMark className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
            <HiOutlineFunnel className="w-3.5 h-3.5" />
            {total} workshop{total !== 1 ? "s" : ""} found
          </div>
        </div>

        {/* Workshop Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader />
            </div>
          ) : workshops.length === 0 ? (
            <div className="p-12 text-center">
              <HiOutlinePencilSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No workshops found</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Name</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Category</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Level</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Axe</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Author</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {workshops.map((workshop) => (
                    <tr
                      key={workshop._id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors group"
                      onClick={() => navigate(`/selecole/editor/${workshop._id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {workshop.image && <img src={workshop.image} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                          <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{workshop.name || <EmptyBadge />}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {workshop.category ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[workshop.category] || "bg-gray-100 text-gray-600"}`}>
                            {workshop.category}
                          </span>
                        ) : (
                          <EmptyBadge />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{workshop.type || <EmptyBadge />}</span>
                      </td>
                      <td className="px-4 py-3">
                        {workshop.level ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${levelColors[workshop.level] || "bg-gray-100 text-gray-600"}`}>
                            {workshop.level}
                          </span>
                        ) : (
                          <EmptyBadge />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{workshop.axe || <EmptyBadge />}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {workshop.user_avatar && <img src={workshop.user_avatar} alt="" className="w-6 h-6 rounded-full object-cover" />}
                          <span className="text-sm text-gray-700">{workshop.user_name || <EmptyBadge />}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-gray-400">{new Date(workshop.created_at).toLocaleDateString()}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {total > PER_PAGE && (
          <div className="mt-6">
            <Pagination
              total={total}
              per_page={PER_PAGE}
              currentPage={page}
              onNext={() => setPage((p) => p + 1)}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <select
      className="rounded-lg border-gray-200 bg-gray-50 text-sm py-2.5 pr-8 pl-3 focus:border-blue-500 focus:ring-blue-500 focus:bg-white transition-colors cursor-pointer"
      value={value}
      onChange={(e) => onChange(e.target.value)}>
      <option value="">All {label.toLowerCase()}s</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt.charAt(0).toUpperCase() + opt.slice(1)}
        </option>
      ))}
    </select>
  );
}

const EmptyBadge = () => <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-300 border border-gray-100">—</span>;

const Create = () => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const navigate = useNavigate();

  async function onSubmit() {
    const { data } = await api.post(`/workshop`, { ...values });
    toast.success("Workshop created successfully");
    setOpen(false);
    navigate(`/selecole/editor/${data._id}`);
  }

  return (
    <>
      <button
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
        onClick={() => setOpen(true)}>
        <HiOutlinePlus className="w-5 h-5" />
        New Workshop
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Create Workshop</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Workshop Name</label>
                <input
                  className="w-full rounded-lg border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-blue-500 focus:bg-white transition-colors"
                  placeholder="Enter workshop name..."
                  value={values.name || ""}
                  onChange={(e) => setValues({ name: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={onSubmit}
                disabled={!values.name?.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all">
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
